import { useUserProfileApi } from '@/api/users';
import { Button } from '@/components/ui/button';
import { FlexCol } from '@/components/ui/flex';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useProfileService,
  useResetOnboardingService,
} from '@/services/regulatoryWatch';
import { useCurrentWorkspaceService } from '@/services/workspaces';
import { Loader2, RotateCcw } from 'lucide-react';
import { useState, type FC } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { regulatoryWatchPaths } from '@/paths';
import { OnboardingWizard } from './components/OnboardingWizard';
import { ResetOnboardingConfirmModal } from './components/ResetOnboardingConfirmModal';

const RW_BASE = '/workspace/:workspaceUname/regulatory-watch';

export const RegulatoryWatchLayout: FC = () => {
  const { isProfileLoading, isOnboardingCompleted } = useProfileService();
  const { resetOnboarding } = useResetOnboardingService();
  const { data: userProfile } = useUserProfileApi();
  const isDittoUser = userProfile?.type === 'ditto';
  const { currentWorkspace } = useCurrentWorkspaceService();
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const isDetailRoute = useMatch(`${RW_BASE}/updates/:updateId`);
  const isReferenceDetailRoute = useMatch(`${RW_BASE}/references/:referenceId`);
  const matchAllDocs = useMatch(
    `${RW_BASE}/${regulatoryWatchPaths.allDocuments}`
  );
  const matchNewsfeed = useMatch(`${RW_BASE}/${regulatoryWatchPaths.newsfeed}`);
  const matchAuditTable = useMatch(
    `${RW_BASE}/${regulatoryWatchPaths.auditTable}`
  );
  const matchAllUpdates = useMatch(
    `${RW_BASE}/${regulatoryWatchPaths.allUpdates}`
  );

  if (isProfileLoading) {
    return (
      <FlexCol alignItems="center" justifyContent="center" className="flex-1">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </FlexCol>
    );
  }

  if (!isOnboardingCompleted) {
    return <OnboardingWizard />;
  }

  if (isDetailRoute || isReferenceDetailRoute) {
    return <Outlet />;
  }

  const handleResetOnboarding = () => {
    resetOnboarding();
    // Also clear persisted onboarding step
    try {
      if (currentWorkspace?.uname) {
        localStorage.removeItem(`onboarding_step:${currentWorkspace.uname}`);
      }
    } catch {}
  };

  const workspaceName = currentWorkspace?.name ?? '';

  const pageTitle = matchAllDocs
    ? 'My references'
    : matchNewsfeed
      ? 'My updates by status'
      : matchAuditTable
        ? 'Audit table'
        : matchAllUpdates
          ? 'My updates'
          : 'Regulatory watch';

  return (
    <FlexCol w="full" className="h-full overflow-hidden">
      <div className="flex items-center shrink-0 h-14 px-6 bg-white border-b border-neutral-200">
        <p className="text-sm font-medium text-neutral-900 tracking-[0.07px] leading-[21px]">
          {pageTitle}
        </p>
        <div className="ml-auto flex items-center gap-2">
          {isDittoUser && (
            <>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon-sm"
                    onClick={() => setIsResetModalOpen(true)}
                  >
                    <RotateCcw size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Reset onboarding</TooltipContent>
              </Tooltip>
              <ResetOnboardingConfirmModal
                open={isResetModalOpen}
                onOpenChange={setIsResetModalOpen}
                workspaceName={workspaceName}
                onConfirm={handleResetOnboarding}
              />
            </>
          )}
        </div>
      </div>

      <Outlet />
    </FlexCol>
  );
};
